<template>
  <div class="public-pass-view">
    <div class="d-flex align-center justify-center min-height-screen bg-grey-lighten-5">
      <VContainer>
        <VRow justify="center">
          <VCol
            cols="12"
            sm="10"
            md="8"
            lg="6"
            xl="5"
          >
            <!-- Loading State -->
            <VCard
              v-if="loading"
              class="pa-8 text-center"
            >
              <VProgressCircular 
                indeterminate 
                color="primary" 
                size="64"
                class="mb-4"
              />
              <p class="text-body-1">
                Loading visitor pass...
              </p>
            </VCard>

            <!-- Error State -->
            <VCard
              v-else-if="error"
              color="error"
              variant="outlined"
              class="pa-6 text-center"
            >
              <VIcon
                icon="tabler-alert-circle"
                size="64"
                class="mb-4 text-error"
              />
              <h3 class="text-h5 mb-3">
                Pass Not Found
              </h3>
              <p class="text-body-2 mb-4">
                {{ error }}
              </p>
              <VBtn 
                color="primary" 
                variant="outlined" 
                @click="$router.push('/')"
              >
                Go to Homepage
              </VBtn>
            </VCard>

            <!-- Visitor Pass Display -->
            <div v-else-if="passData">
              <!-- Header -->
              <VCard
                class="mb-4 text-center"
                elevation="2"
              >
                <VCardText class="pa-6">
                  <VIcon 
                    icon="tabler-hospital" 
                    size="48" 
                    color="primary" 
                    class="mb-3"
                  />
                  <h2 class="text-h4 font-weight-bold text-primary mb-2">
                    Digital Visitor Pass
                  </h2>
                  <p class="text-body-1 text-medium-emphasis">
                    Hospital Visitor Access Pass
                  </p>
                </VCardText>
              </VCard>

              <!-- Visitor Pass Card (Reusing same structure as success page) -->
              <VCard 
                id="public-visitor-pass-card"
                class="visitor-pass mx-auto mb-4" 
                elevation="8"
              >
                <!-- Pass Header -->
                <VCardTitle class="pa-4 text-white pass-header">
                  <div class="d-flex align-center justify-space-between">
                    <div class="d-flex align-center">
                      <VIcon 
                        icon="tabler-hospital" 
                        size="32" 
                        class="me-3"
                      />
                      <div class="text-start">
                        <h5 class="text-h6 font-weight-bold">
                          Hospital Visitor Pass
                        </h5>
                        <p class="text-caption mb-0 pass-subtitle">
                          Present this at reception
                        </p>
                      </div>
                    </div>
                    <VChip
                      :color="passData.status === 'active' ? 'success' : 'warning'"
                      variant="elevated"
                      size="small"
                    >
                      {{ passData.status === 'active' ? 'Active' : 'Expired' }}
                    </VChip>
                  </div>
                </VCardTitle>

                <!-- Pass Content -->
                <VCardText class="pa-6">
                  <!-- Visitor Info -->
                  <div class="d-flex align-center mb-6">
                    <VAvatar
                      color="primary"
                      size="56"
                      class="me-4"
                    >
                      <VIcon
                        icon="tabler-user"
                        size="28"
                      />
                    </VAvatar>
                    
                    <div class="text-start flex-grow-1">
                      <h5 class="text-h5 font-weight-bold mb-1">
                        {{ passData.visitor_name }}
                      </h5>
                      <p class="text-body-2 text-medium-emphasis mb-1">
                        Pass #{{ passData.pass_code }}
                      </p>
                      <p class="text-caption text-medium-emphasis mb-0">
                        Issued: {{ formatDateTime(passData.created_at) }}
                      </p>
                    </div>
                  </div>

                  <!-- QR Code -->
                  <div class="text-center mb-6">
                    <div class="qr-code-container">
                      <VueQrcode
                        :value="qrCodeValue"
                        :options="{
                          width: 200,
                          color: {
                            dark: '#1976d2',
                            light: '#ffffff'
                          },
                          margin: 2
                        }"
                        tag="canvas"
                        class="qr-code-image"
                      />
                      <p class="text-caption text-medium-emphasis mt-3 mb-0">
                        Scan this QR code at entry/exit points
                      </p>
                    </div>
                  </div>

                  <!-- Visit Details -->
                  <VDivider class="mb-4" />
                  
                  <div class="visit-details">
                    <VRow dense>
                      <VCol cols="6">
                        <div class="detail-item">
                          <div class="text-caption text-medium-emphasis">
                            Visiting
                          </div>
                          <div class="text-body-2 font-weight-bold">
                            {{ passData.patient_name || 'N/A' }}
                          </div>
                        </div>
                      </VCol>
                      
                      <VCol cols="6">
                        <div class="detail-item">
                          <div class="text-caption text-medium-emphasis">
                            Department
                          </div>
                          <div class="text-body-2 font-weight-bold">
                            {{ passData.department || 'N/A' }}
                          </div>
                        </div>
                      </VCol>
                      
                      <VCol cols="6">
                        <div class="detail-item">
                          <div class="text-caption text-medium-emphasis">
                            Location
                          </div>
                          <div class="text-body-2 font-weight-bold">
                            {{ passData.location || 'N/A' }}
                          </div>
                        </div>
                      </VCol>
                      
                      <VCol cols="6">
                        <div class="detail-item">
                          <div class="text-caption text-medium-emphasis">
                            UHID
                          </div>
                          <div class="text-body-2 font-weight-bold">
                            {{ passData.uhid || 'N/A' }}
                          </div>
                        </div>
                      </VCol>
                    </VRow>
                  </div>

                  <!-- Validity Information -->
                  <VAlert 
                    :color="passData.status === 'active' ? 'info' : 'warning'" 
                    variant="tonal" 
                    class="mt-4 mb-0"
                    density="compact"
                  >
                    <div class="d-flex align-center">
                      <VIcon
                        :icon="passData.status === 'active' ? 'tabler-clock' : 'tabler-alert-triangle'"
                        class="me-2"
                      />
                      <div>
                        <div class="text-body-2 font-weight-medium">
                          {{ passData.status === 'active' ? 'Valid until:' : 'Expired on:' }} {{ passData.expires_at ? formatDateTime(passData.expires_at) : 'N/A' }}
                        </div>
                        <div class="text-caption">
                          {{ passData.status === 'active' ? 'Present this pass with valid ID' : 'This pass is no longer valid' }}
                        </div>
                      </div>
                    </div>
                  </VAlert>
                </VCardText>
              </VCard>

              <!-- Instructions -->
              <VAlert 
                color="info" 
                variant="outlined"
                class="text-start mb-4"
                prominent
              >
                <template #prepend>
                  <VIcon icon="tabler-info-circle" />
                </template>
                
                <h6 class="text-subtitle-1 font-weight-bold mb-3">
                  Instructions:
                </h6>
                <ol class="text-body-2">
                  <li>Present this digital pass at the hospital reception</li>
                  <li>Bring valid photo identification with you</li>
                  <li>Follow all hospital visitor guidelines and policies</li>
                  <li>Respect visiting hours and patient privacy</li>
                </ol>
              </VAlert>

              <!-- Download/Share Actions -->
              <VCard
                class="pa-4"
                elevation="1"
              >
                <VRow>
                  <VCol
                    cols="12"
                    sm="6"
                  >
                    <VBtn
                      color="primary"
                      variant="outlined"
                      block
                      prepend-icon="tabler-download"
                      @click="downloadPass"
                    >
                      Download Pass
                    </VBtn>
                  </VCol>
                  <VCol
                    cols="12"
                    sm="6"
                  >
                    <VBtn
                      color="success"
                      variant="outlined"
                      block
                      prepend-icon="tabler-share"
                      @click="sharePass"
                    >
                      Share Pass
                    </VBtn>
                  </VCol>
                </VRow>
              </VCard>
            </div>
          </VCol>
        </VRow>
      </VContainer>
    </div>
  </div>
</template>

<script setup>
import { useToast } from '@/composables/useToast'
import VueQrcode from '@chenfengyuan/vue-qrcode'
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'

// Visitors open this pass from a QR code, so it must stay reachable without a session.
// The blank layout is required: the default layout renders the authenticated nav bar,
// which throws on a null user and blanks the page.
definePage({
  meta: {
    layout: 'blank',
    public: true,
    title: 'Visitor Pass',
  },
})

// Composables
const route = useRoute()
const toast = useToast()

// Reactive data
const loading = ref(true)
const error = ref(null)
const passData = ref(null)

// Computed
const qrCodeValue = computed(() => {
  if (!passData.value) return ''
  
  return JSON.stringify({
    pass_code: passData.value.pass_code,
    visitor_name: passData.value.visitor_name,
    patient_name: passData.value.patient_name,
    issued_at: passData.value.created_at,
    expires_at: passData.value.expires_at,
    qr_verification_code: passData.value.qr_code,
  })
})

// Methods
const formatDateTime = dateTime => {
  if (!dateTime) return 'N/A'
  const date = new Date(dateTime)
  
  return date.toLocaleString('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const fetchPassData = async () => {
  try {
    loading.value = true
    error.value = null
    
    const passCode = route.params.code
    if (!passCode) {
      throw new Error('Pass code is required')
    }

    // TODO: Replace with actual API call
    // For now, using mock data - replace this with your actual API endpoint
    const response = await fetch(`/api/visitor-passes/public/${passCode}`)
    
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Visitor pass not found or expired')
      }
      throw new Error('Failed to load visitor pass')
    }
    
    const data = await response.json()

    passData.value = data
    
  } catch (err) {
    console.error('Error fetching pass data:', err)
    error.value = err.message
  } finally {
    loading.value = false
  }
}

const downloadPass = () => {
  // Trigger print dialog for downloading as PDF
  window.print()
}

const sharePass = async () => {
  try {
    const shareData = {
      title: 'Hospital Visitor Pass',
      text: `🏥 Hospital Visitor Pass\n👤 ${passData.value.visitor_name}\n🆔 ${passData.value.pass_code}\n\nView the full pass:`,
      url: window.location.href,
    }

    if (navigator.share) {
      await navigator.share(shareData)
      toast.success('Pass shared successfully!')
    } else {
      // Fallback: copy URL to clipboard
      await navigator.clipboard.writeText(window.location.href)
      toast.success('Pass link copied to clipboard!')
    }
  } catch (err) {
    console.error('Error sharing pass:', err)
    toast.error('Failed to share pass')
  }
}

// Lifecycle
onMounted(() => {
  fetchPassData()
})
</script>

<style scoped>
.min-height-screen {
  min-height: 100vh;
}

/* Reuse visitor pass styles */
.visitor-pass {
  max-width: 420px;
  border-radius: 16px;
  overflow: hidden;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  position: relative;
}

.pass-header {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%) !important;
  position: relative;
  overflow: hidden;
}

.pass-header::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
  animation: shine 3s ease-in-out infinite;
}

.pass-subtitle {
  opacity: 0.9;
}

.qr-code-container {
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: inset 0 2px 4px rgba(0,0,0,0.1);
}

.qr-code-image {
  border-radius: 8px;
}

.detail-item {
  padding: 8px 0;
}

.detail-item:not(:last-child) {
  border-bottom: 1px solid rgba(0,0,0,0.05);
}

@keyframes shine {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Print styles */
@media print {
  .min-height-screen {
    min-height: auto;
  }
  
  #public-visitor-pass-card {
    max-width: none !important;
    box-shadow: none !important;
    border: 2px solid #1976d2 !important;
  }
  
  .pass-header {
    background: #1976d2 !important;
    color: white !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  
  .v-chip {
    background: #4caf50 !important;
    color: white !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  
  .v-avatar {
    background: #1976d2 !important;
    color: white !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  
  .v-alert {
    background: #e3f2fd !important;
    color: #1976d2 !important;
    border-color: #1976d2 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
}
</style>